import { useEffect } from 'react';
import { useDatasetStore } from '@/store/dataset-store';
import { useWidgetsStore } from '@/store/widgets-store';
import { useModeStore } from '@/store/mode-store';
import { configDb } from '@/persistence/config-db';

/**
 * Two lifecycle effects that App.tsx used to inline: widget loading
 * when the active (dataset, mode) changes, and Dexie-side
 * valueGroupings rehydration when the active dataset changes.
 *
 * Pulled into its own module during R-6 because App.tsx had grown
 * past 560 lines and the dependency arrays carried two long-form
 * comments explaining why specific selectors couldn't go in. Keeping
 * the effects co-located with their selectors here makes the
 * "depend on the id, not the whole entry" discipline easier to
 * preserve when the next edit lands.
 */
export function useDashboardLifecycle(): void {
  const activeId = useDatasetStore((s) => s.activeDatasetId);
  const activeColumns = useDatasetStore((s) =>
    s.activeDatasetId
      ? s.datasets.get(s.activeDatasetId)?.columns
      : undefined,
  );
  const setValueGroupings = useDatasetStore((s) => s.setValueGroupings);

  const widgetsDatasetId = useWidgetsStore((s) => s.datasetId);
  const widgetsMode = useWidgetsStore((s) => s.mode);
  const loadForDataset = useWidgetsStore((s) => s.loadForDataset);
  const clearWidgets = useWidgetsStore((s) => s.clear);
  const mode = useModeStore((s) => s.mode);

  // Load / seed widgets when the active (dataset, mode) changes.
  // Depend on the *id* and the columns array specifically — depending
  // on the whole entry would re-fire this effect every time
  // setDatasetProfile or setValueGroupings produces a fresh entry
  // reference (same id, same schema), churning loadForDataset's first
  // synchronous `set({ widgets: [] })` and remounting every chart in
  // a tight cycle.
  useEffect(() => {
    if (!activeId || !activeColumns) {
      clearWidgets();
      return;
    }
    if (widgetsDatasetId !== activeId || widgetsMode !== mode) {
      void loadForDataset(activeId, activeColumns, mode);
    }
  }, [
    activeId,
    activeColumns,
    widgetsDatasetId,
    widgetsMode,
    mode,
    loadForDataset,
    clearWidgets,
  ]);

  // Prime value groupings from Dexie once per active dataset so
  // charts render with mapped labels even before the user opens the
  // editor. Key on `activeId` (not the entry) — setValueGroupings
  // produces a fresh DatasetEntry reference for the same id, so
  // depending on the whole entry would re-fire this effect after
  // every save, read Dexie, call setValueGroupings again, and loop
  // indefinitely.
  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    void configDb.valueGroupings
      .get(activeId)
      .then((entry) => {
        if (cancelled || !entry) return;
        setValueGroupings(activeId, entry.groupings);
      })
      .catch((err: unknown) => {
        console.warn('valueGroupings load failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, [activeId, setValueGroupings]);
}
